import { useNavigate, useLocation } from 'react-router-dom'
import { useCallback } from 'react'
import { useAppContext } from './useAppContext'
import { isReservedSlug } from '@/lib/slugs'

/**
 * Hook for navigating between locations while preserving the current resource.
 *
 * @example
 * const { switchLocation, goToOrgView, currentLocationSlug } = useLocationNavigation()
 *
 * // Switch from /kdet/units to /kphx/units
 * switchLocation('kphx')
 *
 * // Go to org-wide view (e.g., from /kdet/clients to /clients)
 * goToOrgView()
 */
export function useLocationNavigation() {
  const navigate = useNavigate()
  const location = useLocation()
  const { locationSlug: currentLocationSlug, scope, locations } = useAppContext()

  /**
   * Extract the current resource path from the URL
   * @example
   * '/kdet/hangar-view' => 'hangar-view'
   * '/kdet/areas?type=t_hangar' => 'areas'
   * '/dashboard' => 'dashboard'
   */
  const getCurrentResource = useCallback(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean)

    if (scope === 'location' && pathSegments.length >= 2) {
      // Location-scoped: /{loc}/resource/...
      return pathSegments.slice(1).join('/')
    } else if (scope === 'org' && pathSegments.length >= 1) {
      // Org-wide: /resource/...
      return pathSegments.join('/')
    }

    return 'dashboard'
  }, [location.pathname, scope])

  /**
   * Switch to a different location while preserving the current resource
   * @param newLocationSlug The location slug to switch to
   * @param options.resource Optional override for the resource path
   */
  const switchLocation = useCallback(
    (newLocationSlug: string, options?: { resource?: string }) => {
      const resource = options?.resource ?? getCurrentResource()

      // Validate the location exists
      const targetLocation = locations.find(
        (loc) => loc.slug.toLowerCase() === newLocationSlug.toLowerCase()
      )

      if (!targetLocation) {
        console.warn(`Location "${newLocationSlug}" not found`)
        return
      }

      // Check if the resource exists for location-scoped routes
      // Default to dashboard if the current resource is org-only
      const finalResource = isReservedSlug(resource.split('/')[0]) ? 'dashboard' : resource

      navigate(`/${targetLocation.slug}/${finalResource}${location.search}`)
    },
    [getCurrentResource, locations, navigate, location.search]
  )

  /**
   * Navigate to the org-wide view of the current resource
   * @example
   * From /kdet/clients → /clients
   * From /kdet/dashboard → /dashboard
   */
  const goToOrgView = useCallback(
    (options?: { resource?: string }) => {
      const resource = options?.resource ?? getCurrentResource()

      // Some resources don't have org-wide equivalents
      const orgWideResources = ['dashboard', 'clients', 'aircraft', 'leases', 'contacts', 'locations']

      const firstSegment = resource.split('/')[0]

      if (orgWideResources.includes(firstSegment)) {
        navigate(`/${resource}${location.search}`)
      } else {
        // Default to dashboard for location-only resources
        navigate('/dashboard')
      }
    },
    [getCurrentResource, navigate, location.search]
  )

  /**
   * Navigate to a specific location's dashboard
   */
  const goToLocationDashboard = useCallback(
    (locationSlug: string) => {
      switchLocation(locationSlug, { resource: 'dashboard' })
    },
    [switchLocation]
  )

  /**
   * Get the default location (first available)
   */
  const getDefaultLocation = useCallback(() => {
    return locations[0] ?? null
  }, [locations])

  return {
    currentLocationSlug,
    scope,
    locations,
    switchLocation,
    goToOrgView,
    goToLocationDashboard,
    getDefaultLocation,
    getCurrentResource,
  }
}
